/**
 * Favorites Page
 */

import { X } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ToolRegistry } from '@/engine/ToolRegistry';
import type { ITool } from '@/interfaces/ITool';
import { setStorageItem } from '@/lib/storageUtils';
import { useAppStore } from '@/store/useAppStore';
import { useEditorStore } from '@/store/useEditorStore';
import { useToolBrowserStore } from '@/store/useToolBrowserStore';

function toolMatchesSearch(tool: ITool, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const haystack = [tool.name, tool.description, tool.category, ...(tool.keywords ?? [])]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

function ToolTile({ tool, onClick }: { tool: ITool; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="text-left border border-border rounded-lg p-4 bg-muted/10 hover:bg-muted/30 transition-colors"
      title={tool.id}
    >
      <div className="font-medium">{tool.name}</div>
      <div className="text-sm text-muted-foreground mt-1">{tool.description}</div>
    </button>
  );
}

export const FavoritesPage: React.FC<{ onNavigate: (to: string) => void }> = ({
  onNavigate,
}) => {
  const { addTool } = useAppStore();
  const favoriteIds = useToolBrowserStore((s) => s.favorites);
  const pipeline = useEditorStore((s) => s.pipeline);
  const [query, setQuery] = useState('');

  const favorites = useMemo(() => {
    return favoriteIds
      .map((id) => ToolRegistry.get(id))
      .filter(Boolean) as ITool[];
  }, [favoriteIds]);

  const filtered = useMemo(() => {
    return favorites
      .filter((t) => toolMatchesSearch(t, query))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [favorites, query]);

  const handleUseTool = (toolId: string) => {
    setStorageItem('app.selectedToolId', toolId);
    onNavigate('/');
  };

  return (
    <div className="h-full overflow-auto">
      <TooltipProvider>
      <div className="max-w-6xl mx-auto p-8">
        <h1 className="text-3xl font-semibold">Favorites</h1>

        <div className="mt-6">
          <div className="relative">
            <Input
              placeholder="Search favorites..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={query ? 'pr-9' : undefined}
            />
            {query ? (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
                onClick={() => setQuery('')}
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>

        <div className="mt-8">
          {favorites.length === 0 ? (
            <div className="text-sm text-muted-foreground">No favorites configured.</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-muted-foreground">No matching favorites.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {filtered.map((tool) => (
                <div key={tool.id} className="relative">
                  <ToolTile tool={tool} onClick={() => handleUseTool(tool.id)} />
                  {pipeline.length >= 1 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="secondary"
                          size="sm"
                          className="absolute top-3 right-3"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            addTool(tool.id);
                          }}
                          aria-label="Add to pipeline"
                        >
                          +
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">Add to pipeline</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </TooltipProvider>
    </div>
  );
};
